/** Transforma o Documento (blocos com ligações) em blocos compostos,
 *  já resolvidos e quebrados, prontos a paginar.
 *
 *  Fica à parte do modelo e da paginação porque é a ponte entre os
 *  dois: é aqui que um "tabela-classes" deixa de ser uma intenção e
 *  passa a ser linhas com colunas.
 */

import {
  type BlocoComposto,
  ESTILOS,
  quebrarTexto,
} from '@viewer/report/documentLayout'
import {
  type BlocoDocumento,
  type DadosDocumento,
  type Documento,
  formatarNumero,
  resolverTexto,
} from '@viewer/report/documentModel'

/** Onde começa a coluna dos números, contada da margem esquerda. */
export const COLUNA_NUMEROS_MM = 120

/** Folga entre o fim da primeira coluna e o início da segunda. */
const MARGEM_ENTRE_COLUNAS_MM = 5

function estiloDoBloco(tipo: BlocoDocumento['tipo']) {
  if (tipo === 'titulo') return ESTILOS.titulo
  if (tipo === 'subtitulo') return ESTILOS.subtitulo
  if (tipo === 'tabela-classes' || tipo === 'topicos-bcf') return ESTILOS.tabela
  return ESTILOS.paragrafo
}

export function compor(
  documento: Documento,
  dados: DadosDocumento,
  larguraUtilMm: number,
): BlocoComposto[] {
  const compostos: BlocoComposto[] = []

  for (const bloco of documento.blocos) {
    const estilo = estiloDoBloco(bloco.tipo)

    if (bloco.tipo === 'quebra') {
      // Uma quebra é um bloco alto de propósito: empurra o que vem a
      // seguir para a página seguinte sem precisar de um conceito novo
      // na paginação.
      compostos.push({
        linhas: [{ texto: '', xMm: 0 }],
        tamanhoMm: 10_000,
        negrito: false,
        antesMm: 0,
        depoisMm: 0,
        ehTitulo: false,
      })
      continue
    }

    if (bloco.tipo === 'tabela-classes') {
      const linhas: { texto: string; xMm: number }[] = []
      if (dados.porClasse.length === 0) {
        linhas.push({ texto: 'Sem elementos para listar.', xMm: 0 })
      } else {
        for (const linha of dados.porClasse) {
          linhas.push({ texto: linha.classe.replace(/^IFC/, ''), xMm: 0 })
          linhas.push({
            texto: formatarNumero(linha.contagem),
            xMm: COLUNA_NUMEROS_MM,
          })
        }
      }
      compostos.push({
        linhas,
        tamanhoMm: estilo.tamanhoMm,
        negrito: false,
        antesMm: estilo.antesMm,
        depoisMm: estilo.depoisMm,
        ehTitulo: false,
        cabecalho: [
          { texto: 'Classe IFC', xMm: 0 },
          { texto: 'Elementos', xMm: COLUNA_NUMEROS_MM },
        ],
      })
      continue
    }

    if (bloco.tipo === 'topicos-bcf') {
      const linhas: { texto: string; xMm: number }[] = []
      if (dados.topicos.length === 0) {
        linhas.push({ texto: 'Sem tópicos BCF.', xMm: 0 })
      } else {
        for (const t of dados.topicos) {
          // O título quebra-se à largura da primeira coluna: inteiro, um
          // título comprido escrevia-se por cima do estado.
          const [primeira, ...resto] = quebrarTexto(
            t.titulo,
            COLUNA_NUMEROS_MM - MARGEM_ENTRE_COLUNAS_MM,
            estilo.tamanhoMm,
          )
          linhas.push({ texto: primeira, xMm: 0 })
          linhas.push({
            texto: [t.estado, t.responsavel].filter(Boolean).join(' · ') || '-',
            xMm: COLUNA_NUMEROS_MM,
          })
          for (const r of resto) linhas.push({ texto: r, xMm: 0 })
        }
      }
      compostos.push({
        linhas,
        tamanhoMm: estilo.tamanhoMm,
        negrito: false,
        antesMm: estilo.antesMm,
        depoisMm: estilo.depoisMm,
        ehTitulo: false,
        cabecalho: [
          { texto: 'Tópico', xMm: 0 },
          { texto: 'Estado', xMm: COLUNA_NUMEROS_MM },
        ],
      })
      continue
    }

    const resolvido = resolverTexto(bloco.texto, dados)
    compostos.push({
      linhas: quebrarTexto(resolvido, larguraUtilMm, estilo.tamanhoMm).map(
        (texto) => ({ texto, xMm: 0 }),
      ),
      tamanhoMm: estilo.tamanhoMm,
      negrito: estilo.negrito,
      antesMm: estilo.antesMm,
      depoisMm: estilo.depoisMm,
      ehTitulo: bloco.tipo === 'titulo' || bloco.tipo === 'subtitulo',
    })
  }

  return compostos
}
